/**
 * Shapes for the System Analytics screen.
 *
 * Mirrors `SystemAnalyticsCommentResponseDTO` / `SystemAnalyticsAdminRowDTO` /
 * `SystemAnalyticsSummaryDTO` on the backend. Field names are the wire names - the RestService layer
 * does no camelCase conversion, so the backend DTOs are annotated-free and the TS names match exactly.
 */

/**
 * What a note is about. Mirrors `SystemAnalyticsTopic`.
 *
 * An unknown value from the server degrades to OTHER rather than being rejected: a note that was
 * already written must still render, even if a later release adds a topic this build has never heard
 * of. Hiding an existing note would be worse than showing it with the wrong chip.
 */
export enum SystemAnalyticsTopic {
  CREDIT_EXHAUSTION = "CREDIT_EXHAUSTION",
  GROWTH = "GROWTH",
  OUTREACH = "OUTREACH",
  PLATFORM = "PLATFORM",
  OTHER = "OTHER",
}

export const SYSTEM_ANALYTICS_TOPIC_LABELS: Record<SystemAnalyticsTopic, string> = {
  [SystemAnalyticsTopic.CREDIT_EXHAUSTION]: "Credit exhaustion",
  [SystemAnalyticsTopic.GROWTH]: "Growth",
  [SystemAnalyticsTopic.OUTREACH]: "Outreach",
  [SystemAnalyticsTopic.PLATFORM]: "Platform",
  [SystemAnalyticsTopic.OTHER]: "Other",
};

/**
 * Admin roles. Only these two ever appear in the roster - candidates and recruiters are excluded
 * server-side by an allow-list, not by the client hiding them, so the guarantee does not depend on
 * this file being loaded.
 */
export const ADMIN_ROLES = ["SUPER_ADMIN", "DELEGATED_ADMIN"] as const;

export type AdminRole = (typeof ADMIN_ROLES)[number];

export interface SystemAnalyticsCommentDTO {
  id: string;
  /** Null when the author's account has since been deleted. */
  authorId: string | null;
  /** Denormalised so a deleted author does not blank the note. */
  authorName: string;
  authorRole: string;
  topic: string;
  subjectRole: string | null;
  body: string;
  createdAt: string | null;
  updatedAt: string | null;
  edited: boolean;
}

export interface SystemAnalyticsAdminRowDTO {
  /** Undashed hex from the backend; use `toUuid` before treating it as a UUID. */
  userId: string;
  email: string;
  fullName: string;
  role: string;
  userStatus: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  /** COUNT(*) in SQL, so a BigInteger on the wire but a plain number in JSON. */
  noteCount: number;
}

export interface SystemAnalyticsSummaryDTO {
  totalNotes: number;
  notesLast7Days: number;
  notesLast30Days: number;
  totalAdmins: number;
  superAdmins: number;
  delegatedAdmins: number;
  activeContributors: number;
  availableRoles: string[];
}

export interface SystemAnalyticsCommentRequest {
  body: string;
  topic: string;
  subjectRole?: string | null;
}

/**
 * Outcome of a delete. `deleted` is false when the note was already soft-deleted - the request
 * succeeds either way, so the flag is what distinguishes "just removed" from "already gone".
 */
export interface SystemAnalyticsDeleteResultDTO {
  commentId: string;
  deletedAt: string | null;
  deleted: boolean;
}